<template>
  <div class="d-flex align-items-end gap-3">
    <!-- 썸네일 -->
    <div
      class="thumb bg-light d-flex align-items-center justify-content-center border position-relative rounded-3"
      :style="avatarStyle"
      :class="{ clickable: !disabled }"
      role="button"
      :aria-label="`${label} 선택`"
      :tabindex="disabled ? -1 : 0"
      @click="!disabled && onClickSelect()"
      @keydown.enter.prevent="!disabled && onClickSelect()"
      @keydown.space.prevent="!disabled && onClickSelect()"
    >
      <img
        v-if="displayPreview"
        :src="displayPreview"
        class="w-100 h-100 object-fit-cover rounded-3"
        :alt="alt"
        draggable="false"
      />
      <span v-else class="text-secondary">No Image</span>
    </div>

    <!-- 우측: 파일 선택/표시 -->
    <div class="flex-grow-1">
      <label class="form-label mb-1">{{ label }}</label>

      <!-- ✅ 컨트롤 줄: 감기지 않게 -->
      <div class="d-flex align-items-center gap-2 flex-nowrap">
        <div
          class="input-group flex-grow-1"
          :style="{ maxWidth: inputWidth }"
        >
          <input
            type="text"
            class="form-control"
            :value="displayName"
            :placeholder="placeholder"
            readonly
          />
          <button
            type="button"
            class="btn btn-outline-secondary mb-0"
            @click="onClickSelect"
            :disabled="disabled"
          >
            {{ selectText }}
          </button>
        </div>

        <!-- 숨김 파일 인풋 -->
        <input
          ref="fileInput"
          type="file"
          :accept="accept"
          class="d-none"
          @change="onChangeFile"
          :disabled="disabled"
        />

        <!-- ✅ 버튼이 줄바꿈되지 않도록 shrink 방지 클래스 추가 -->
        <soft-button
          class="no-shrink"
          variant="outline"
          color="secondary"
          :min-width="80"
          @click="clear"
          :disabled="disabled"
        >
          {{ clearText }}
        </soft-button>
      </div>

      <!-- ✅ 힌트는 컨트롤 줄 밖(아래)으로 -->
      <label class="form-label text-muted d-block mt-1 mb-0">
        <slot name="hint">{{ hint }}</slot>
      </label>
    </div>

  </div>
</template>

<script>
import SoftButton from "@/components/SoftButton.vue";
export default {
  name: "ImagePicker",
  props: {
    file: { type: File, default: null },              // v-model:file
    existingPreview: { type: String, default: "" },   // 서버 프리뷰 URL
    existingName: { type: String, default: "" },      // 서버 원본 파일명
    label: { type: String, default: "대표 이미지" },
    alt: { type: String, default: "image" },
    placeholder: { type: String, default: "선택된 파일 없음" },
    selectText: { type: String, default: "선택" },
    clearText: { type: String, default: "제거" },
    hint: { type: String, default: "PNG/JPG, 10MB 이하 권장" },
    accept: { type: String, default: "image/*" },
    maxSize: { type: Number, default: 10 * 1024 * 1024 },
    disabled: { type: Boolean, default: false },
    avatarWidth: { type: Number, default: 120 },
    avatarHeight: { type: Number, default: 120 },
    inputWidth: { type: String, default: "320px" },
  },
  emits: ["update:file", "selected", "cleared", "error"],
  components: { SoftButton },
  data() {
    return { blobPreview: "" };
  },
  computed: {
    displayName() {
      return this.file?.name || this.existingName || "";
    },
    displayPreview() {
      return this.blobPreview || this.existingPreview || "";
    },
    avatarStyle() {
      return { width: this.avatarWidth + "px", height: this.avatarHeight + "px" };
    },
  },
  methods: {
    onClickSelect() {
      this.$refs.fileInput?.click();
    },
    onChangeFile(e) {
      const f = e.target.files?.[0];
      if (!f) return;

      if (this.accept && !new RegExp(this.accept.replace("*", ".*")).test(f.type)) {
        this.$emit("error", "이미지 파일만 업로드 가능합니다.");
        e.target.value = ""; // ✅ input 초기화
        return;
      }
      if (this.maxSize && f.size > this.maxSize) {
        this.$emit("error", `파일은 ${Math.floor(this.maxSize / (1024 * 1024))}MB 이하만 가능합니다.`);
        e.target.value = ""; // ✅ input 초기화
        return;
      }

      if (this.blobPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(this.blobPreview);
      }

      this.blobPreview = URL.createObjectURL(f);

      this.$emit("update:file", f);
      this.$emit("selected", { file: f, previewUrl: this.blobPreview, name: f.name });

      e.target.value = "";
    },
    clear() {
      if (this.blobPreview?.startsWith("blob:")) URL.revokeObjectURL(this.blobPreview);
      this.blobPreview = "";
      this.$emit("update:file", null);
      this.$emit("cleared");
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
  },
  beforeUnmount() {
    if (this.blobPreview?.startsWith("blob:")) URL.revokeObjectURL(this.blobPreview);
  },
};
</script>

<style scoped>
.thumb {
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  will-change: transform;
  user-select: none;
}
.clickable { cursor: pointer; }
.clickable:hover { transform: scale(1.1); }
.clickable:active { transform: scale(0.99); }
.no-shrink {
  flex-shrink: 0;
}
@media (max-width: 576px) {
  .input-group.flex-grow-1 {
    max-width: 100% !important;
  }
}
</style>
